import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Grid, Paper } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumFormaPago } from '../../../api/Argentia/enums'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import { ContractModel, PaymentMethodTypeModel } from '../../../api/Argentia/model/contracts'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../../redux'
import { checkValidIBAN, fetchContractsBySubjectID } from '../../contracts/contracts.service'
import { mapContractsToSelectOptions } from '../../procedures/procedures.mapper'
import {
  mapSettlementGroupToRequest,
  paymentMethodTypesToOptionsMapper
} from '../settlementGroups.mapper'
import { SettlementGroupAggregatedAddFormValidations } from '../settlementGroups.model'
import {
  createSettlementGroup,
  fetchPaymentMethodsTypes,
  fetchPostalCodes,
  getPostalAddressByID,
  isSettlementGroupCodeDuplicated
} from '../settlementGroups.service'
import { fetchSubjects } from '../../../services/subjects.service'

export interface SettlementGroupAggregatedAndVirtualAddFormDataType {
  code: string | undefined
  description: string | undefined
  typeSettlementGroup: EnumTypeSettlementGroup | undefined

  subject: SelectOption | undefined
  contracts: number[]
  startDate: Date | undefined
  endDate: Date | undefined

  street: string | undefined
  postalCodeSearch: SelectOption | undefined
  postalCode: string | undefined
  municipality: string | undefined
  province: string | undefined
  country: string | undefined
  municipalityId: number | undefined
  provinceId: number | undefined
  countryId: number | undefined

  paymentMethod: SelectOption | undefined
  iban: string | undefined
}

const INTIAL_VALIDATIONS: SettlementGroupAggregatedAddFormValidations = {
  all: false,
  iban: true,
  postalCode: false,
  subject: false
}

export interface SettlementGroupAggregatedAddPageProps {
  location?: {
    state?: {
      settlementGroupCodes?: { id: number; code: string }[]
    }
  }
}

export const SettlementGroupAggregatedAddPage: React.FC<
  SettlementGroupAggregatedAddPageProps
> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'settlementGroups', 'enums'])
  const [formData, setFormData] = useState<SettlementGroupAggregatedAndVirtualAddFormDataType>(
    {} as SettlementGroupAggregatedAndVirtualAddFormDataType
  )
  const [formAddValidations, setFormAddValidations] =
    useState<SettlementGroupAggregatedAddFormValidations>(INTIAL_VALIDATIONS)
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodTypeModel[]>([])
  const [subjectContracts, setSubjectContracts] = useState<ContractModel[]>([])

  const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false)
  const [formIsLoading, setFormIsLoading] = useState<boolean>(true)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [ibanRequired, setIbanRequired] = useState<boolean>(false)
  const [ibanIsValid, setIbanIsValid] = useState<boolean | undefined>(false)

  const history = useHistory()
  const { info, error } = useNotify()
  const { getInformation, getConfirmation, closeConfirmation, closeInformation } =
    useConfirmationDialog()

  // Handlers Starts

  const handleFormSave = async () => {
    if (formData) {
      if (!missingFieldsFormWithRequiredText(formAddSection, formData)) {
        if (
          isSettlementGroupCodeDuplicated(
            formData.code,
            props.location?.state?.settlementGroupCodes ?? []
          )
        ) {
          error(t('settlementGroups:notifications.create.duplicatedCode', { code: formData.code }))
          return true
        }
        setIsFetching(true)
        await createSettlementGroup(mapSettlementGroupToRequest(formData)).then(response => {
          setIsFetching(false)
          if (response.success) {
            info(t('settlementGroups:forms.add.save.success'))
            history.push(NonAuthRoutes.SettlementGroups)
          } else {
            error(t('settlementGroups:forms.add.save.error'))
          }
        })
      } else {
        error(t('settlementGroups:forms.add.save.incompleteForm'))
      }
      return true
    } else {
      history.goBack()
      return false
    }
  }

  const handleCancelClick = () => {
    if (formData) {
      getConfirmation(ConfirmationDialogs.CANCEL_PENDING_CHANGES).then(async () => {
        closeConfirmation()
        history.goBack()
      })
    } else {
      history.goBack()
    }
  }
  // Handlers Ends

  // Form Definition Starts

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:forms.add.analytics')
    }
  }

  const formAddSection: Section[] = [
    {
      title: t('settlementGroups:forms.add.title.settlementGroup'),
      inputs: [
        {
          name: 'code',
          label: t('settlementGroups:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'description',
          label: t('settlementGroups:forms.add.inputs.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'typeSettlementGroup',
          label: t('settlementGroups:forms.add.inputs.typeSettlementGroup'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTypeSettlementGroup,
            'enums:EnumTypeSettlementGroup'
          ).filter(
            e =>
              e.value === EnumTypeSettlementGroup.FacturacionAgregada ||
              e.value === EnumTypeSettlementGroup.FacturacionResumen
          ),
          required: true
        },
        {
          name: 'subject',
          label: t('settlementGroups:forms.add.inputs.subject'),
          type: DataType.SEARCHFIELD,
          placeholder: t('settlementGroups:forms.add.placeholders.subject'),
          apiCall: fetchSubjects,
          onChange: (subjectSelected: any, formState: any) => {
            if (subjectSelected) {
              validateField('subject', true)
              //clean previous contracts from FormData
              setFormData({
                ...formState,
                contracts: []
              })
              //update form MULTISELECT values if field is not Null
              getSubjectContracts(subjectSelected.value)
            } else {
              validateField('subject', false)
              setSubjectContracts([])
            }
          },
          required: true,
          clearable: true
        },
        {
          name: 'contracts',
          label: t('settlementGroups:forms.add.inputs.contracts'),
          type: DataType.MULTISELECT,
          isLoading: isLoadingContracts,
          selectOptions: mapContractsToSelectOptions(subjectContracts)
        },
        {
          name: 'startDate',
          label: t('settlementGroups:forms.add.inputs.startDate'),
          type: DataType.DATE
        },
        {
          name: 'endDate',
          label: t('settlementGroups:forms.add.inputs.endDate'),
          type: DataType.DATE
        }
      ]
    },
    {
      title: t('settlementGroups:forms.add.title.paymentMethod'),
      inputs: [
        {
          name: 'paymentMethod',
          label: t('settlementGroups:forms.add.inputs.paymentMethod'),
          type: DataType.SELECT,
          selectOptions: paymentMethodTypesToOptionsMapper(paymentMethodOptions),
          onChange: (paymentMethodSelected, formState: any) => {
            handleIbanRequired(+paymentMethodSelected)
            if (formState.iban !== undefined && formState.iban !== '') {
              //Force iban re-check if it is not empty
              handleNewIban(formState.iban)
            }
          },
          clearable: true,
          required: true
        },
        {
          name: 'iban',
          label: t('settlementGroups:forms.add.inputs.iban'),
          type: DataType.TEXT,
          onBlur: iban => {
            if (iban === '' && !ibanRequired) {
              validateField('iban', true)
              return
            } else {
              handleNewIban(`${iban}`)
            }
          },
          required: ibanRequired,
          clearable: true
        }
      ]
    },
    {
      title: t('settlementGroups:forms.add.title.billingDirection'),
      inputs: [
        {
          name: 'postalCodeSearch',
          label: t('settlementGroups:forms.add.inputs.postalCode'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchPostalCodes,
          maxLength: 5,
          required: true,
          clearable: true,
          onChange: (apiCallObject: any, formState: any) => {
            updateAddress(apiCallObject, formState)
          }
        },
        {
          name: 'municipality',
          label: t('settlementGroups:forms.add.inputs.municipality'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'province',
          label: t('settlementGroups:forms.add.inputs.province'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'country',
          label: t('settlementGroups:forms.add.inputs.country'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'street',
          label: t('settlementGroups:forms.add.inputs.street'),
          type: DataType.TEXT,
          required: true
        }
        // {
        //   name: 'useExistingAccount',
        //   label: t('contracts:forms.add.inputs.useExistingAccount'),
        //   type: DataType.SWITCH,
        //   readOnly: !clientData?.id,
        //   onChange: value => {
        //     if (value) {
        //       handleShowExistingAccounts()
        //     }
        //   }
        // }
      ]
    }
  ]
  // Form Definition Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts

  const validateField = (validation: 'iban' | 'postalCode' | 'subject', isValid: boolean) => {
    setFormAddValidations(prevState => {
      const newValidation = { ...prevState, [validation]: isValid }
      const formIsValid = newValidation.iban && newValidation.postalCode
      return { ...newValidation, all: formIsValid }
    })
  }

  const updateAddress = async (apiCallObject: any, formState: any) => {
    if (apiCallObject !== undefined && apiCallObject !== null) {
      setIsFetching(true)
      const postalAddress = await getPostalAddressByID(+apiCallObject.value)
        .catch(e => {
          validateField('postalCode', false)
        })
        .finally(() => setIsFetching(false))

      if (postalAddress) {
        //update Form State
        setFormData({
          ...formState,
          municipality: postalAddress.municipality.description,
          province: postalAddress.province.description,
          country: postalAddress.country.description,
          municipalityId: postalAddress.municipality.id,
          provinceId: postalAddress.province.id,
          countryId: postalAddress.country.id,
          postalCode: postalAddress.postalCode
        })
        validateField('postalCode', true)
      }
    } else {
      // if field is NULL, then clean dependant Form fields
      setFormData({
        ...formState,
        // street: undefined,
        municipality: undefined,
        province: undefined,
        country: undefined,
        municipalityId: undefined,
        provinceId: undefined,
        countryId: undefined,
        postalCode: undefined
      })
      validateField('postalCode', false)
    }
  }

  const handleNewIban = async (iban: string) => {
    setIsFetching(true)
    const response = await checkValidIBAN(iban).finally(() => setIsFetching(false))

    if (response.success && response.data) {
      if (response.data.isValid) {
        setIbanIsValid(true)
        validateField('iban', true)
      } else {
        error(t('settlementGroups:forms.add.validations.ibanError'))
        setIbanIsValid(false)
        validateField('iban', false)
      }
    } else {
      error(t('settlementGroups:forms.add.validations.ibanFetch'))
      setIbanIsValid(undefined)
      validateField('iban', false)
    }
  }

  const handleIbanRequired = (paymentMethodSelected: number) => {
    const paymentMethod = paymentMethodOptions.find(
      paymentMethod => paymentMethod.Id === paymentMethodSelected
    )

    if (paymentMethod && paymentMethod.methodType === EnumFormaPago.Domiciliacion) {
      setIbanRequired(true)
    } else {
      ibanRequired && setIbanRequired(false)
      validateField('iban', true)
    }
  }

  const getSubjectContracts = subjectId => {
    setIsLoadingContracts(true)
    setIsFetching(true)
    fetchContractsBySubjectID(subjectId)
      .then(response => {
        if (response.success) {
          response.data && setSubjectContracts(response.data)
        } else {
          error(t('settlementGroups:forms.add.validations.subjectContracts'))
        }
        setIsLoadingContracts(false)
      })
      .finally(() => setIsFetching(false))
  }
  // Functions Ends

  // Hooks Starts
  useEffect(() => {
    const getPaymentMethods = async () => {
      setFormIsLoading(true)
      await fetchPaymentMethodsTypes()
        .then(response => {
          if (response.success && response.data !== undefined) {
            setPaymentMethodOptions(response.data)
          } else {
            error(t('settlementGroups:forms.add.validations.paymentMethodFetchError'))
          }
        })
        .finally(() => setFormIsLoading(false))
    }
    getPaymentMethods()
  }, [])
  // Hooks Ends

  return (
    <Page>
      <PageHeader title={t('settlementGroups:forms.add.headerTitle')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={formIsLoading || isFetching}
          onClick={handleCancelClick}
        >
          {t('settlementGroups:forms.add.buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={formIsLoading || isFetching || !formAddValidations.all}
          // hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={handleFormSave}
        >
          {t('settlementGroups:forms.add.buttons.save')}
        </Button>
      </PageHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={6}>
            {formIsLoading ? (
              <>
                <FormSkeleton columns={3} inputs={6} elevation={2} withTitle={true} />
                <FormSkeleton columns={3} inputs={9} elevation={2} withTitle={true} />
              </>
            ) : (
              <Form
                title={t('settlementGroups:forms.add.formTitle')}
                sections={formAddSection}
                data={formData}
                options={options}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Page>
  )
}
